/**
 * Default store for widgets section
 */

import api from "@/api";
import CacheHelper from "@/cacheHelper";
import { mergeWidgets, filterWidgets, UUID, trimLayout } from "./helpers";

const cacheConfig = {
  key: "USER_DASHBOARD",
  key_admin: "ADMIN_USER_DASHBOARD",
  ttl: 60 * 60 * 12 * 1000, // 12 hours
};
const cache = new CacheHelper(cacheConfig);

// global api endpoint for dashboard
const endpoint = "/currentuser/dashboard";
const adminEndpoint = "/dashboard";

// base class for dashboard / widgets
const widgetStore = {
  namespaced: true,
  state: {
    dashboard: [],
    currentDashboardState: null,
    adminDashboard: null,
  },
  actions: {
    /**
     * Get the dashboard from the API
     * TODO: Need a fallback in case there is nothing set up yet
     */
    async getUserDashboard({ commit, state }, ignoreState = true) {
      let response;
      let elements = [];

      if (cache.existsAndIsValid()) {
        elements = cache.getItem(cacheConfig.key);
        commit("SET_DASHBOARD", elements);
        commit("SET_DASHBOARD_STATE", elements);
        return Promise.resolve(elements);
      }

      // if there is no cache, check if there is something in state
      if (
        state.currentDashboardState &&
        state.currentDashboardState.length > 0 &&
        !ignoreState
      ) {
        elements = state.currentDashboardState;

        // at this point put back in storage
        cache.setItem(cacheConfig.key, elements, true);
        commit("SET_DASHBOARD", elements);
        return Promise.resolve(elements);
      }

      try {
        response = await api().get(endpoint);
        elements = response.data.data.dashboard[0].elements
          ? response.data.data.dashboard[0].elements
          : [];

        commit("SET_DASHBOARD", elements);
        commit("SET_DASHBOARD_STATE", elements);
        cache.setItem(cacheConfig.key, elements, true);
        return Promise.resolve(elements);
      } catch (err) {
        // fall back to state default
        cache.setItem(cacheConfig.key, [], true);
        commit("SET_DASHBOARD", []);
        commit("SET_DASHBOARD_STATE", []);
        return Promise.resolve([]);
      }
    },

    /**
     * Get widgets defined in admin area, split into header/main/footer
     * Dont want to cache if we are in admin area
     */
    async getAdminUserDashboard({ commit, state }, options) {
      let response;
      let elements = [];

      if (!options || !options.isAdmin) {
        if (cache.existsAndIsValid(cacheConfig.key_admin)) {
          elements = cache.getItem(cacheConfig.key_admin);
          commit("SET_ADMIN_DASHBOARD", elements);
          return Promise.resolve(elements);
        }

        // if there is no cache, check if there is something in state
        // not if the one in the state is the default dashboard
        if (
          state.adminDashboard &&
          state.adminDashboard.length > 0 &&
          JSON.stringify(state.adminDashboard) !== JSON.stringify([])
        ) {
          elements = state.adminDashboard;

          // at this point put back in storage
          cache.setItem(cacheConfig.key_admin, elements, true);
          commit("SET_ADMIN_DASHBOARD", elements);
          return Promise.resolve(elements);
        }
      }

      try {
        response = await api().get("/dashboard");
        elements = response.data.data.dashboard[0].elements;

        commit("SET_ADMIN_DASHBOARD", elements);
        if (!options || !options.isAdmin) {
          cache.setItem(cacheConfig.key_admin, elements, true);
        }
        return Promise.resolve(elements);
      } catch (err) {
        // fall back to state default
        if (!options || !options.isAdmin) {
          cache.setItem(cacheConfig.key_admin, [], true);
        }
        return Promise.resolve([]);
      }
    },

    // Get elements determined by the admin area
    //TODO: Do we want to cache this? We do for the front end but what about the admin?
    async setUserDashboard(context, { layouts, admin }) {
      const rowLabels = ["headerRow", "mainRow", "footerRow"];
      const setAdmin = admin ? admin : false;
      let response;

      const payload = {
        dashboard: {
          elements: [],
        },
      };

      let trimmedLayout;
      Object.keys(layouts).forEach((layout, index) => {
        // need to trim any empty rows
        trimmedLayout = trimLayout(layouts[layout]);
        if (trimmedLayout && trimmedLayout.length > 0) {
          trimmedLayout.forEach((row, counter) => {
            const elements = row.map((r) => {
              let props = [];
              if (r.props) {
                props = r.props;
              }

              /**
               * Set the admin status for the widget
               * If we are coming from the admin section, set it to true
               * If we are coming from the user section, check it's type then fall back to false
               */
              let adminStatus;
              if (setAdmin) {
                adminStatus = setAdmin;
              } else {
                adminStatus = r.admin ? r.admin : false;
              }

              let uuid;

              if (r.uuid) {
                uuid = r.uuid;
              } else if (r.uuid == "" && r.type == "") {
                uuid = "";
              } else {
                // if for some reason there is a type and there is no uuid, create one
                uuid = UUID.generate();
              }

              return `{'type':'${r.type}', 'props':${JSON.stringify(
                props
              ).replace(
                /'/g,
                '"'
              )}, 'admin': '${adminStatus}', 'uuid':'${uuid}'}`;
            });
            if (row[0]) {
              payload.dashboard.elements.push({
                type: rowLabels[index],
                sort_order: counter,
                properties: {
                  columns: `{'layout':${row[0].cssClass},'cols':[${elements}]}`,
                },
                content: "",
              });
            }
          });
        }
      });

      try {
        response = await api().post(
          setAdmin ? adminEndpoint : endpoint,
          payload
        );

        return Promise.resolve(response);
      } catch (err) {
        return Promise.reject(err);
      }
    },

    resetDashboard({ state, commit }) {
      commit("SET_DASHBOARD", state.currentDashboardState);
    },
    async reloadDashboard({ dispatch, commit }, admin = false) {
      const action = admin ? "getAdminUserDashboard" : "getUserDashboard";
      const args = admin ? null : {isAdmin: true};
      const key = admin ? cacheConfig.key_admin : cacheConfig.key;
      const mutation = admin ? "SET_ADMIN_DASHBOARD" : "SET_DASHBOARD_STATE";
      cache.removeItem(key);
      //remove current dashboard state too
      commit(mutation, null);

      return Promise.resolve();
      let response;

      try {
        response = await dispatch(action, args);
        return Promise.resolve(response);
      } catch (err) {
        return Promise.reject(err);
      }
    },

    clearDashboard({ commit }) {
      commit("SET_DASHBOARD", []);
      commit("SET_DASHBOARD_STATE", null);
      commit("SET_ADMIN_DASHBOARD", null);
    },
  },
  mutations: {
    SET_DASHBOARD(state, payload) {
      state.dashboard = payload;
    },
    SET_DASHBOARD_STATE(state, payload) {
      state.currentDashboardState = payload;
    },
    SET_ADMIN_DASHBOARD(state, payload) {
      state.adminDashboard = payload;
    },
  },
  getters: {
    dashboard: (state) => state.dashboard,
    currentSavedState: (state) => state.currentDashboardState,
    userHeaderWidgets: (state, rootGetters) => {
      if (!state.dashboard) {
        return [];
      }

      return filterWidgets(state.dashboard, "headerRow", rootGetters);
    },
    adminHeaderWidgets: (state, rootGetters) => {
      if (!state.adminDashboard) {
        return [];
      }

      return filterWidgets(
        state.adminDashboard,
        "headerRow",
        rootGetters,
        true
      );
    },
    mergedHeaderWidgets: (state, getters) => {
      return mergeWidgets(
        getters["adminHeaderWidgets"],
        getters["userHeaderWidgets"]
      );
    },
    userMainWidgets: (state, rootGetters) => {
      let widgets = filterWidgets(state.dashboard, "mainRow", rootGetters);
      return widgets
      
    },
    adminMainWidgets: (state, rootGetters) => {
      return filterWidgets(state.adminDashboard, "mainRow", rootGetters, true);
    },
    mergedMainWidgets: (state, getters) => {
      // merged main widgets are a bit different
      // We want to grab the layout from both bits, but the user section is primary
      // Any admin bits just need to be flagged as admin, not put in place
      return mergeWidgets(
        getters["adminMainWidgets"],
        getters["userMainWidgets"],
        false
      );
    },
    userFooterWidgets: (state, rootGetters) => {
      return filterWidgets(state.dashboard, "footerRow", rootGetters);
    },
    adminFooterWidgets: (state, rootGetters) => {
      return filterWidgets(
        state.adminDashboard,
        "footerRow",
        rootGetters,
        true
      );
    },
    mergedFooterWidgets: (state, getters) => {
      return mergeWidgets(
        getters["adminFooterWidgets"],
        getters["userFooterWidgets"]
      );
    },
    getWidgetsByType: (state, getters) => (type) => {
      const layout = [
        ...getters["mergedHeaderWidgets"],
        ...getters["mergedMainWidgets"],
        ...getters["mergedFooterWidgets"],
      ];

      const widgets = [];

      layout.forEach((row) => {
        row.forEach((col) => {
          if (col.type === type) {
            widgets.push(col);
          }
        });
      });

      return widgets;
    },
  },
};

export default widgetStore;
